import { i18next } from '@main/i18next';
import { ParseKeys } from 'i18next';
import { z } from 'zod/v4';

const uploadedFileSchema = z.object({
  id: z.string(),
  name: z.string().optional().nullable(),
  size: z.number().optional().nullable(),
});

/**
 * Schema for single file upload with size validation.
 * By default, file is REQUIRED (undefined fails validation).
 * Use `.optional()` to make file optional.
 *
 * @param maxFileSize - Maximum file size in MB (default: 250)
 * @param customErrorMessage - Error message when file exceeds size limit
 */
type UploadedFile = z.infer<typeof uploadedFileSchema>;
type FileValue = File | UploadedFile;

export const singleFileSchema = (
  maxFileSize = 250,
  errorMessage: ParseKeys = 'form.uploadLimit',
) => {
  const maxFileSizeBytes = maxFileSize * 1024 * 1024;

  return z
    .custom<FileValue | undefined>((file) => {
      // Accept undefined (will be caught by refine below for required fields)
      if (file === undefined) {
        return true;
      }
      // Accept File objects
      if (file instanceof File) {
        return true;
      }
      // Accept uploaded file objects with id
      if (file && typeof file === 'object' && 'id' in file && typeof file.id === 'string') {
        return true;
      }
      return false;
    })
    .refine((val) => val !== undefined, { error: () => i18next.t('form.fields.required.label') })
    .refine((val) => !val || !(val instanceof File) || val.size <= maxFileSizeBytes, {
      error: () => i18next.t(errorMessage),
    });
};

/**
 * Schema for multiple file uploads with size validation.
 * Use .min(1) for to make it required.
 *
 * @param maxFileSize - Maximum file size per file in MB (default: 250)
 * @param errorMessage - Error message when any file exceeds size limit
 */
export const multiFileSchema = (
  maxFileSize = 250,
  errorMessage: ParseKeys = 'form.uploadLimit',
) => {
  const maxFileSizeBytes = maxFileSize * 1024 * 1024;

  return z
    .array(z.union([z.instanceof(File), uploadedFileSchema]))
    .refine(
      (files) => files.filter((f) => f instanceof File).every((f) => f.size <= maxFileSizeBytes),
      {
        error: () => i18next.t(errorMessage),
      },
    );
};
